import * as React from "react";
import { X } from "lucide-react";

import { cn } from "@sunfish/lib/utils";

export interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  /**
   * Turns raw typed or pasted text into the entries to add. Returning an empty
   * array rejects the text, which the caller reports through `error`.
   */
  parse?: (raw: string) => string[];
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  error?: string | null;
  "aria-describedby"?: string;
  className?: string;
}

const defaultParse = (raw: string) =>
  raw
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);

/**
 * A list of short values built one chip at a time. Entries commit on Enter, Tab,
 * comma and blur, so a value left in the box when the form is saved is not
 * silently dropped.
 */
const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      value,
      onChange,
      parse = defaultParse,
      placeholder,
      id,
      disabled,
      error,
      className,
      ...props
    },
    ref,
  ) => {
    const [draft, setDraft] = React.useState("");
    const [rejected, setRejected] = React.useState(false);

    const commit = (raw: string) => {
      const parsed = parse(raw);
      if (parsed.length === 0) {
        setRejected(raw.trim().length > 0);
        return raw.trim().length === 0;
      }
      const next = [...value];
      for (const entry of parsed) {
        if (!next.includes(entry)) next.push(entry);
      }
      onChange(next);
      setRejected(false);
      return true;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        if (commit(draft)) setDraft("");
        return;
      }
      if (event.key === "Backspace" && draft === "" && value.length > 0) {
        onChange(value.slice(0, -1));
      }
    };

    return (
      <div className={cn("space-y-1.5", className)}>
        <div
          className={cn(
            "flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring",
            error && rejected && "border-destructive",
            disabled && "cursor-not-allowed opacity-50",
          )}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              event.currentTarget.querySelector("input")?.focus();
            }
          }}
        >
          {value.map((entry) => (
            <span
              key={entry}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground"
            >
              {entry}
              <button
                type="button"
                aria-label={`Remove ${entry}`}
                disabled={disabled}
                className="text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none"
                onClick={() => onChange(value.filter((v) => v !== entry))}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          <input
            ref={ref}
            id={id}
            disabled={disabled}
            value={draft}
            placeholder={value.length === 0 ? placeholder : undefined}
            className="h-6 min-w-32 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed sm:text-sm"
            onChange={(event) => {
              setDraft(event.target.value);
              if (rejected) setRejected(false);
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (commit(draft)) setDraft("");
            }}
            onPaste={(event) => {
              const text = event.clipboardData.getData("text");
              if (!text.trim()) return;
              event.preventDefault();
              if (commit(`${draft}${text}`)) setDraft("");
            }}
            {...props}
          />
        </div>
        {error && rejected && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  },
);
TagInput.displayName = "TagInput";

export { TagInput };
